import type { PackVariantSnapshot } from '@/domain/entities/pack.entity';
import type { OptionDisplayDefinition, OptionDisplayType } from './option-display-type';
import { resolveOptionDisplayType } from './option-display-type';

export type { OptionDisplayDefinition, OptionDisplayType } from './option-display-type';
export { isColorOptionName, isSwatchOption, resolveOptionDisplayType } from './option-display-type';

export interface ProductOptionShape {
  name: string;
  values: string[];
  display_type: OptionDisplayType;
}

export function buildOptionsFromVariants(
  variants: PackVariantSnapshot[],
  productOptions?: OptionDisplayDefinition[],
): ProductOptionShape[] {
  const optionMap = new Map<string, Set<string>>();

  for (const variant of variants) {
    if (!variant.enabled) continue;
    for (const attr of variant.variation_attributes ?? []) {
      if (!optionMap.has(attr.name)) {
        optionMap.set(attr.name, new Set());
      }
      optionMap.get(attr.name)?.add(attr.value);
    }
  }

  return Array.from(optionMap.entries()).map(([name, values]) => ({
    name,
    values: Array.from(values),
    display_type: resolveOptionDisplayType(name, productOptions),
  }));
}

export function resolveVariantFromOptions(
  variants: PackVariantSnapshot[],
  selectedVariantId: string | undefined,
  optionName: string,
  optionValue: string,
): PackVariantSnapshot | undefined {
  const selected = variants.find((v) => v.id === selectedVariantId && v.enabled);

  const currentSelections: Record<string, string> = {};
  if (selected?.variation_attributes) {
    for (const attr of selected.variation_attributes) {
      currentSelections[attr.name] = attr.value;
    }
  }

  currentSelections[optionName] = optionValue;

  let matching = variants.find((v) => {
    if (!v.enabled) return false;
    return Object.entries(currentSelections).every(([name, value]) => {
      const attr = v.variation_attributes?.find((a) => a.name === name);
      return attr?.value === value;
    });
  });

  if (!matching) {
    matching = variants.find((v) => {
      if (!v.enabled) return false;
      const attr = v.variation_attributes?.find((a) => a.name === optionName);
      return attr?.value === optionValue;
    });
  }

  return matching;
}
